import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import layoutConfig from '../config/layout';
import Sidebar from '../components/layout/Sidebar';
import Footer from '../components/layout/Footer';
import PublicSiteHeader from '../components/layout/PublicSiteHeader';
import { resolveRoleKey, ROLE, selectUser } from '../auth';
import { FaBars } from "react-icons/fa";

const MainLayout = ({ children, config }) => {
  const user = useSelector(selectUser);
  const roleKey = resolveRoleKey(user);
  const isReader = roleKey === ROLE.READER;
  const isWriter = roleKey === ROLE.WRITER;
  const isAdmin = roleKey === ROLE.SUPERADMIN;
  const cfg = { showSearch: true, ...layoutConfig, ...config };
  const isAuthenticated = !!user?.uid;
  /** Public header is only for guests (no "topbar" for authenticated users). */
  const showPublicHeader = !isAuthenticated && cfg.showPublicHeader !== false;
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("lw:sidebar:collapsed") === "1";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("lw:sidebar:collapsed", desktopSidebarCollapsed ? "1" : "0");
  }, [desktopSidebarCollapsed]);

  /** App sidebar for all authenticated roles (reader/writer/admin). */
  const effectiveShowSidebar = isAuthenticated ? isWriter || isAdmin || isReader : !!cfg.showSidebar;
  const contentTopPadding = showPublicHeader
    ? 'calc(4rem + env(safe-area-inset-top))'
    : 'calc(0.75rem + env(safe-area-inset-top))';
  const showMobileSidebarHeader = effectiveShowSidebar;

  return (
    <div className="d-flex flex-column min-vh-100 theme-transition app-page">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      {showMobileSidebarHeader && (
        <div className="mobile-layout-header d-lg-none">
          <button
            type="button"
            className="mobile-layout-header__hamburger"
            aria-label="Open menu"
            onClick={() => setMobileSidebarOpen(true)}
          >
            <FaBars />
          </button>
          <div className="mobile-layout-header__title">
          {isWriter ? `${user?.displayName}'s Workspace` : isAdmin ? "Admin Panel" : isReader ? "Menu" : "Menu"}
          </div>
        </div>
      )}
      {showPublicHeader ? <PublicSiteHeader /> : null}
      {effectiveShowSidebar && (
        <>
          <div
            className={`mobile-sidebar-backdrop d-lg-none ${mobileSidebarOpen ? "show" : ""}`}
            onClick={() => setMobileSidebarOpen(false)}
            role="presentation"
          />
          <aside className={`mobile-sidebar-drawer d-lg-none ${mobileSidebarOpen ? "show" : ""}`}>
            <div className="mobile-sidebar-drawer__header">
              <div className="mobile-sidebar-drawer__brand" aria-label="Sidebar logo">
                {String(user?.sidebarLogoUrl || "").trim() ? (
                  <img src={String(user?.sidebarLogoUrl || "").trim()} alt="Sidebar logo" className="mobile-sidebar-drawer__brand-img" />
                ) : (
                  <span className="mobile-sidebar-drawer__brand-fallback">
                    <i className="bi bi-image" aria-hidden />
                  </span>
                )}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary mobile-sidebar-drawer__close-btn"
                onClick={() => setMobileSidebarOpen(false)}
                aria-label="Close menu"
                title="Close menu"
              >
                <i className="bi bi-chevron-left" aria-hidden />
              </button>
            </div>
            <Sidebar showIcons={true} onNavigate={() => setMobileSidebarOpen(false)} />
          </aside>
        </>
      )}

      <div className="container-fluid flex-grow-1 pt-3 pt-md-4 pt-lg-4 pb-5 pb-md-5 pb-lg-0" style={{ paddingTop: showMobileSidebarHeader ? "0.8rem" : contentTopPadding, paddingBottom: 'calc(5rem + env(safe-area-inset-bottom))', backgroundColor: 'var(--color-bg)' }}>
        <div className="row g-0 g-lg-2 app-layout-row">
          {effectiveShowSidebar && (
            <div
              className={`d-none d-lg-block app-layout-sidebar-col ${desktopSidebarCollapsed ? "app-layout-sidebar-col--collapsed" : ""} ${isAdmin ? "app-layout-sidebar-col--admin" : ""}`}
            >
              <div className={`desktop-sidebar-shell ${desktopSidebarCollapsed ? "desktop-sidebar-shell--collapsed" : ""}`}>
                <Sidebar
                  showIcons={true}
                  collapsed={desktopSidebarCollapsed}
                  onToggleCollapse={() => setDesktopSidebarCollapsed((v) => !v)}
                  showCollapseToggle
                />
              </div>
            </div>
          )}
          <main
            id="main-content"
            role="main"
            tabIndex="-1"
            className={[
              effectiveShowSidebar
                ? "col-12 app-layout-main-col px-1 px-md-2 px-lg-2 app-page"
                : "col-12 px-1 px-md-2 px-lg-2 app-page",
              showPublicHeader ? "app-layout--public-header" : "",
              effectiveShowSidebar ? "app-layout--writer-sidebar" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {children}
          </main>
        </div>
      </div>
      {cfg.showFooter && <Footer />}
    </div>
  );
};

export default MainLayout;
